// Platform Admin — Pricing Config Panel (Hybrid: Form + Advanced JSON + Audit Log)
// Single Source of Truth: /api/platform-admin/pricing/override
import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_META = [
  { key: 'min_wallet_recharge', label: 'Min Wallet Recharge (₹)', min: 1, max: 1000000, type: 'number' },
  { key: 'currency', label: 'Currency Code (ISO)', type: 'text', maxLength: 3 },
  { key: 'currency_symbol', label: 'Currency Symbol', type: 'text', maxLength: 3 },
];

export default function PricingConfigPanel({ token, language = 'hi' }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [data, setData] = useState({ defaults: {}, override: {}, effective: {}, last_updated_at: null, last_updated_by: null });
  const [form, setForm] = useState({});
  const [changeNote, setChangeNote] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/platform-admin/pricing/override`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) {
        setData(d);
        const merged = { ...d.defaults, ...d.override };
        setForm(merged);
        setJsonText(JSON.stringify(merged, null, 2));
      }
    } catch (e) { setMsg({ type: 'error', text: 'Load failed: ' + e.message }); }
    setLoading(false);
  };

  const loadAudit = async () => {
    try {
      const r = await fetch(`${API_URL}/api/platform-admin/pricing/audit?limit=50`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) setAudit(d.items || []);
    } catch (e) { console.warn('audit load:', e); }
  };

  useEffect(() => { loadData(); }, []);

  const setField = (k, v) => {
    const newForm = { ...form, [k]: v };
    setForm(newForm);
    setJsonText(JSON.stringify(newForm, null, 2));
    setJsonError('');
  };

  const handleJsonEdit = (text) => {
    setJsonText(text);
    try {
      const parsed = JSON.parse(text);
      setJsonError('');
      setForm(parsed);
    } catch (e) {
      setJsonError('Invalid JSON: ' + e.message);
    }
  };

  const buildDiff = () => {
    const eff = data.effective || {};
    const out = {};
    FIELD_META.forEach(({ key }) => {
      const cur = form[key];
      const effVal = eff[key];
      if (cur !== undefined && cur !== '' && String(cur) !== String(effVal)) {
        out[key] = typeof effVal === 'number' ? Number(cur) : cur;
      }
    });
    return out;
  };

  const save = async () => {
    if (jsonError) { setMsg({ type: 'error', text: 'Fix JSON errors first' }); return; }
    const diff = buildDiff();
    if (Object.keys(diff).length === 0) {
      setMsg({ type: 'info', text: language === 'hi' ? 'कोई बदलाव नहीं है' : 'No changes to save' });
      return;
    }
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`${API_URL}/api/platform-admin/pricing/override`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...diff, change_note: changeNote || null }),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        setMsg({ type: 'success', text: (language === 'hi' ? '✅ Saved — ' : '✅ Updated — ') + d.changed_fields.join(', ') });
        setChangeNote('');
        await loadData();
      } else {
        setMsg({ type: 'error', text: 'Save failed: ' + (d.detail?.[0]?.msg || d.detail || 'unknown') });
      }
    } catch (e) { setMsg({ type: 'error', text: 'Save error: ' + e.message }); }
    setSaving(false);
  };

  const reset = async () => {
    if (!window.confirm(language === 'hi' ? 'सभी overrides हटाकर defaults पर reset करें?' : 'Remove all overrides and reset to defaults?')) return;
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`${API_URL}/api/platform-admin/pricing/override/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ change_note: changeNote || 'Reset to defaults' }),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        setMsg({ type: 'success', text: '✅ Reset to defaults' });
        setChangeNote('');
        await loadData();
      }
    } catch (e) { setMsg({ type: 'error', text: 'Reset error: ' + e.message }); }
    setSaving(false);
  };

  if (loading) return <div className="p-6 text-gray-500">Loading pricing config…</div>;

  const eff = data.effective || {};
  const isOverridden = Object.keys(data.override || {}).length > 0;

  return (
    <div className="space-y-4" data-testid="pricing-config-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-xl font-bold text-gray-800" style={{ fontFamily: 'Outfit' }}>
            💰 {language === 'hi' ? 'Pricing Configuration' : 'Pricing Configuration'}
          </h3>
          <p className="text-xs text-gray-500">
            {language === 'hi'
              ? 'एक ही जगह से पूरे platform की pricing control करें। बदलाव तुरंत reflect होंगे।'
              : 'Control platform-wide pricing from one place. Changes reflect instantly everywhere.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isOverridden ? (
            <span className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded-full font-semibold" data-testid="override-active-badge">⚠️ Override Active</span>
          ) : (
            <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-semibold">✓ Using Defaults</span>
          )}
          <button onClick={() => { setShowAudit(!showAudit); if (!showAudit) loadAudit(); }} className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg font-medium" data-testid="toggle-audit-btn">
            📝 {language === 'hi' ? 'Audit History' : 'Audit History'}
          </button>
        </div>
      </div>

      {/* Slab pricing — source of truth (read-only) */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4" data-testid="slab-tiers-info">
        <p className="text-xs font-semibold text-indigo-900 mb-1">📊 {language === 'hi' ? 'Slab Pricing (source of truth)' : 'Slab Pricing (source of truth)'}</p>
        <p className="text-[11px] text-indigo-700 mb-3">
          {language === 'hi'
            ? 'Software FREE। कीमत flat होती है — कुल users (छात्र+शिक्षक+staff) के slab पर। Founding offer बंद है। ये tiers code (config/pricing.py) में manage होते हैं।'
            : 'Software FREE. Flat price by total-users (students+teachers+staff) slab. Founding offer disabled. Tiers are managed in code (config/pricing.py).'}
        </p>
        {Array.isArray(eff?.pricing_tiers) && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {eff.pricing_tiers.map((tr, i) => (
              <div key={i} className="bg-white/70 rounded-md px-2 py-1.5 text-[11px]">
                <div className="font-semibold text-gray-800">{tr.label_hi || tr.label_en}</div>
                <div className="text-indigo-700">
                  {tr.custom ? (language === 'hi' ? 'Custom (Sales)' : 'Custom (Sales)')
                    : `${eff.currency_symbol}${Number(tr.one_time || 0).toLocaleString('en-IN')} + ${eff.currency_symbol}${Number(tr.annual_maintenance || 0).toLocaleString('en-IN')}/yr`}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Current Effective Values */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-200">
        <p className="text-xs font-semibold text-orange-800 mb-2 uppercase tracking-wider">{language === 'hi' ? 'अभी Active Values' : 'Currently Active Values'}</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><span className="text-gray-500">{language === 'hi' ? 'Base slab (1–300 users):' : 'Base slab (1–300 users):'}</span> <span className="font-bold">{eff?.display?.standard_rate}</span></div>
          <div><span className="text-gray-500">One-time:</span> <span className="font-bold">{eff?.currency_symbol}{eff?.one_time_price}</span></div>
          <div><span className="text-gray-500">Maintenance/yr:</span> <span className="font-bold text-green-700">{eff?.currency_symbol}{eff?.annual_maintenance}</span></div>
          <div><span className="text-gray-500">Min Recharge:</span> <span className="font-bold">{eff?.currency_symbol}{eff?.min_wallet_recharge}</span></div>
        </div>
        {data.last_updated_at && (
          <p className="text-[11px] text-gray-500 mt-2">
            {language === 'hi' ? 'अंतिम बदलाव:' : 'Last changed:'} {new Date(data.last_updated_at).toLocaleString()} — {data.last_updated_by}
          </p>
        )}
      </div>

      {/* Form Fields */}
      <div className="bg-white rounded-xl p-5 border">
        <h4 className="font-semibold text-gray-800 mb-3">{language === 'hi' ? 'Editable Fields' : 'Editable Fields'}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {FIELD_META.map(({ key, label, min, max, type, maxLength }) => {
            const isOverriddenField = data.override && key in data.override;
            return (
              <div key={key} className="space-y-1">
                <label className="flex items-center justify-between text-xs font-medium text-gray-700">
                  <span>{label}</span>
                  {isOverriddenField && <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">override</span>}
                </label>
                <input
                  type={type}
                  min={min}
                  max={max}
                  maxLength={maxLength}
                  value={form[key] ?? ''}
                  onChange={(e) => setField(key, type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  data-testid={`pricing-field-${key}`}
                />
                <p className="text-[10px] text-gray-400">Default: {String(data.defaults?.[key])}</p>
              </div>
            );
          })}
        </div>

        {/* Advanced JSON editor */}
        <div className="mt-4 border-t pt-3">
          <button onClick={() => setShowAdvanced(!showAdvanced)} className="text-xs font-semibold text-slate-600 hover:text-slate-900" data-testid="toggle-advanced-json">
            {showAdvanced ? '▼' : '▶'} {language === 'hi' ? 'Advanced — Full JSON Editor' : 'Advanced — Full JSON Editor'}
          </button>
          {showAdvanced && (
            <div className="mt-2 space-y-1">
              <textarea
                value={jsonText}
                onChange={(e) => handleJsonEdit(e.target.value)}
                rows={10}
                spellCheck={false}
                className={`w-full px-3 py-2 font-mono text-xs border-2 rounded-lg ${jsonError ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-gray-50'}`}
                data-testid="pricing-json-editor"
              />
              {jsonError && <p className="text-xs text-red-700" data-testid="json-error">{jsonError}</p>}
              <p className="text-[10px] text-gray-500">
                ⚠️ {language === 'hi' ? 'सावधान — सीधे JSON edit करने पर type/range validation backend पर होता है। गलत value → 422 error।' : 'Caution — direct JSON edit; type/range validation happens at backend. Invalid values → 422.'}
              </p>
            </div>
          )}
        </div>

        {/* Change note + actions */}
        <div className="mt-4 space-y-2">
          <input
            type="text"
            placeholder={language === 'hi' ? 'Change Note (optional — audit log के लिए)' : 'Change Note (optional — for audit log)'}
            value={changeNote}
            onChange={(e) => setChangeNote(e.target.value)}
            maxLength={500}
            className="w-full px-3 py-2 border rounded-lg text-sm"
            data-testid="pricing-change-note"
          />
          {msg && (
            <div className={`text-xs px-3 py-2 rounded-lg ${msg.type === 'success' ? 'bg-green-50 text-green-800' : msg.type === 'error' ? 'bg-red-50 text-red-800' : 'bg-blue-50 text-blue-800'}`} data-testid="pricing-message">
              {msg.text}
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={saving || !!jsonError}
              className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg disabled:opacity-50"
              data-testid="save-pricing-btn"
            >
              {saving ? 'Saving…' : (language === 'hi' ? 'Save Changes' : 'Save Changes')}
            </button>
            <button
              onClick={reset}
              disabled={saving || !isOverridden}
              className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg text-sm disabled:opacity-50"
              data-testid="reset-pricing-btn"
            >
              {language === 'hi' ? 'Defaults पर Reset' : 'Reset to Defaults'}
            </button>
          </div>
        </div>
      </div>

      {/* Audit log */}
      {showAudit && (
        <div className="bg-white rounded-xl p-5 border" data-testid="audit-log">
          <h4 className="font-semibold text-gray-800 mb-3">📝 {language === 'hi' ? 'Change History' : 'Change History'}</h4>
          {audit.length === 0 ? (
            <p className="text-sm text-gray-500">{language === 'hi' ? 'अभी कोई history नहीं है।' : 'No history yet.'}</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {audit.map((a) => (
                <div key={a.id} className="border rounded-lg p-3 text-xs">
                  <div className="flex justify-between items-center flex-wrap gap-2">
                    <span className={`px-2 py-0.5 rounded-full font-semibold ${a.action === 'reset' ? 'bg-slate-100 text-slate-700' : 'bg-orange-100 text-orange-700'}`}>
                      {a.action.toUpperCase()}
                    </span>
                    <span className="text-gray-500">{new Date(a.updated_at).toLocaleString()} — {a.updated_by}</span>
                  </div>
                  <p className="text-gray-700 mt-1">Changed: <strong>{(a.changed_fields || []).join(', ')}</strong></p>
                  {a.change_note && <p className="text-gray-500 italic mt-1">"{a.change_note}"</p>}
                  <details className="mt-1">
                    <summary className="text-[11px] text-slate-500 cursor-pointer">Before → After diff</summary>
                    <pre className="text-[10px] bg-gray-50 p-2 mt-1 rounded overflow-x-auto">{JSON.stringify({ before: a.before_effective, after: a.after_effective }, null, 2)}</pre>
                  </details>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
